import React, { useEffect, useMemo, useState } from 'react';
import { Check, Cloud, Image as ImageIcon, Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';

import { mediaAPI } from '../lib/api';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';


const previewUrl = (url) => {
  if (!url || !url.includes('/image/upload/')) return url;
  return url.replace('/image/upload/', '/image/upload/f_auto,q_auto:eco,c_limit,w_520,h_520/');
};

const formatBytes = (bytes = 0) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function CloudinaryMediaLibraryDialog({
  open,
  onOpenChange,
  onSelect,
  multiple = false,
  maxSelection = 1,
  title = 'Choose from Cloudinary',
  description = 'Reuse an image already uploaded by this company. The original Cloudinary file is not duplicated.',
}) {
  const [assets, setAssets] = useState([]);
  const [nextCursor, setNextCursor] = useState('');
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = async ({ append = false, cursor = '' } = {}) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const response = await mediaAPI.listLibrary({ nextCursor: cursor, maxResults: 60 });
      const incoming = response.data?.assets || [];
      setAssets((current) => {
        const merged = append ? [...current, ...incoming] : incoming;
        const seen = new Set();
        return merged.filter((asset) => {
          if (!asset?.public_id || seen.has(asset.public_id)) return false;
          seen.add(asset.public_id);
          return true;
        });
      });
      setNextCursor(response.data?.next_cursor || '');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Cloudinary media library could not be loaded');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setQuery('');
    load();
  }, [open]);

  const filteredAssets = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return assets;
    return assets.filter((asset) => (
      `${asset.original_filename || ''} ${asset.public_id || ''} ${asset.format || ''}`
        .toLowerCase()
        .includes(value)
    ));
  }, [assets, query]);

  const toggle = (asset) => {
    if (!multiple) {
      setSelected([asset]);
      return;
    }
    setSelected((current) => {
      const exists = current.some((item) => item.public_id === asset.public_id);
      if (exists) return current.filter((item) => item.public_id !== asset.public_id);
      if (current.length >= maxSelection) {
        toast.warning(`You can select up to ${maxSelection} image${maxSelection === 1 ? '' : 's'}`);
        return current;
      }
      return [...current, asset];
    });
  };

  const confirm = () => {
    if (!selected.length) return;
    onSelect?.(multiple ? selected : selected[0]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] grid-rows-[auto_auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:h-[92vh] sm:max-h-[92vh] sm:w-[94vw] sm:max-w-[94vw] lg:max-w-[1400px]">
        <DialogHeader className="border-b bg-white px-4 py-3 pr-12 sm:px-6 sm:py-4 sm:pr-14">
          <DialogTitle className="flex items-center gap-2"><Cloud className="h-5 w-5 text-indigo-600" />{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="border-b bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search filename or Cloudinary public ID" className="pl-9" />
            </div>
            <Button type="button" variant="outline" onClick={() => load()} disabled={loading} className="shrink-0">
              {loading ? <Loader2 className="animate-spin" /> : <RefreshCw />} Refresh
            </Button>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto bg-slate-50 p-3 sm:p-4">
          {loading ? (
            <div className="flex min-h-72 items-center justify-center text-slate-500"><Loader2 className="mr-2 h-6 w-6 animate-spin" />Loading Cloudinary images…</div>
          ) : filteredAssets.length === 0 ? (
            <div className="flex min-h-72 flex-col items-center justify-center text-center text-slate-500">
              <ImageIcon className="mb-3 h-10 w-10" />
              <p className="font-medium">No matching images found</p>
              <p className="text-sm">Upload a new image first, or clear the search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7">
              {filteredAssets.map((asset) => {
                const active = selected.some((item) => item.public_id === asset.public_id);
                return (
                  <button
                    key={asset.public_id}
                    type="button"
                    onClick={() => toggle(asset)}
                    onDoubleClick={() => {
                      if (!multiple) {
                        onSelect?.(asset);
                        onOpenChange(false);
                      }
                    }}
                    aria-pressed={active}
                    className={`group overflow-hidden rounded-lg border bg-white text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${active ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-indigo-300 hover:shadow-md'}`}
                  >
                    <div className="relative aspect-square bg-slate-100">
                      <img src={previewUrl(asset.url)} alt={asset.original_filename || 'Cloudinary image'} className="h-full w-full object-contain" loading="lazy" />
                      {active ? <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-white shadow"><Check className="h-4 w-4" /></span> : null}
                      {!multiple ? <span className="absolute inset-x-2 bottom-2 rounded bg-black/70 px-2 py-1 text-center text-[11px] font-medium text-white opacity-0 transition group-hover:opacity-100">Double-click to use</span> : null}
                    </div>
                    <div className="space-y-1 p-2.5">
                      <p className="truncate text-xs font-medium text-slate-900" title={asset.original_filename || asset.public_id}>{asset.original_filename || asset.public_id?.split('/').pop()}</p>
                      <p className="text-[11px] text-slate-500">{asset.width || 0} × {asset.height || 0}{asset.bytes ? ` · ${formatBytes(asset.bytes)}` : ''}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {nextCursor && !query ? (
            <div className="mt-4 flex justify-center pb-1">
              <Button type="button" variant="outline" onClick={() => load({ append: true, cursor: nextCursor })} disabled={loadingMore}>
                {loadingMore ? <Loader2 className="animate-spin" /> : null} Load more
              </Button>
            </div>
          ) : null}
        </div>

        <DialogFooter className="flex-row flex-wrap items-center gap-2 border-t bg-white px-4 py-3 shadow-[0_-4px_12px_rgba(15,23,42,0.06)] sm:px-6 sm:space-x-0">
          <div className="mr-auto min-w-0 text-sm text-slate-500">
            {selected.length ? <span className="font-medium text-indigo-700">{selected.length} selected</span> : multiple ? `Select up to ${maxSelection}` : 'Select one image'}
          </div>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={confirm} disabled={!selected.length}>Use selected image{multiple && selected.length !== 1 ? 's' : ''}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
